// src/lib/analytics/commercial-events.ts
//
// Pure decision rules for the three additive SEO measurement events
// (`commercial_cta_click`, `signup_intent`, `sign_up`). No `window`, no React,
// no gtag call: `landing-link-analytics.tsx` and `sign-up-form.tsx` own the
// wiring and call these functions with plain values, so the decisions stay
// testable with literal tables — this repo's Vitest config runs with
// `environment: "node"` (no jsdom), so nothing inside a `.tsx` is reachable by
// a test.
//
// These events are additive measurement only. They share no code with the
// PostHog `cta_click` / `signup_*` families and never change what those send.

export const COMMERCIAL_EVENTS = {
  CTA_CLICK: "commercial_cta_click",
  SIGNUP_INTENT: "signup_intent",
  SIGN_UP: "sign_up",
} as const;

/**
 * `cta_placement`. The first six are the markers the landing surface already
 * renders as `data-cta-placement` (read, never edited — PostHog's `cta_click`
 * owns them); the next four are this event's own `data-commercial-cta`
 * markers. `"unknown"` is deliberate rather than a dropped event: an untagged
 * commercial CTA has to show up in GA4 so it can be found and tagged.
 */
export type CommercialPlacement =
  | "header_primary"
  | "hero_primary"
  | "hero_secondary"
  | "sticky_primary"
  | "sticky_docs"
  | "hero"
  | "home_section"
  | "acf_section"
  | "acf_card"
  | "acf_final"
  | "blog_inline"
  | "unknown";

const COMMERCIAL_PLACEMENTS: ReadonlySet<string> = new Set<CommercialPlacement>([
  "header_primary",
  "hero_primary",
  "hero_secondary",
  "sticky_primary",
  "sticky_docs",
  "hero",
  "home_section",
  "acf_section",
  "acf_card",
  "acf_final",
  "blog_inline",
]);

/** A known marker, or `"unknown"` for anything absent, blank or unrecognised. */
export function commercialPlacement(raw: string | null | undefined): CommercialPlacement {
  if (typeof raw !== "string") return "unknown";
  const value = raw.trim();
  return COMMERCIAL_PLACEMENTS.has(value) ? (value as CommercialPlacement) : "unknown";
}

export type CtaDestinationType = "signup" | "enquiry_form" | "telegram";

// Lets a relative href be parsed by the same `new URL` as an absolute one. A
// relative href keeps this host, which matches none of the rules below, so it
// can only ever be classified by its pathname.
const RELATIVE_BASE = "https://relative.invalid";

/**
 * The three commercial destinations, from the href alone. `null` means "not a
 * commercial CTA" and the caller sends nothing — that is what keeps Sign in,
 * `/docs`, the support bubble, "See Card Payments" and internal blog links
 * silent without naming any of them here.
 *
 * Only the TYPE is ever returned, never the href, so no path, query string or
 * fragment can reach a GA4 parameter through this function. Never throws.
 */
export function ctaDestinationType(href: string | null | undefined): CtaDestinationType | null {
  if (typeof href !== "string" || href.trim() === "") return null;
  let url: URL;
  try {
    url = new URL(href.trim(), RELATIVE_BASE);
  } catch {
    return null;
  }
  // Rules out `mailto:` (the FAQ's email contact) and `tel:`.
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;

  const host = url.host.toLowerCase();
  if (host === "t.me") return "telegram";
  if (host === "forms.gle") return "enquiry_form";
  if (host === "docs.google.com" && /^\/forms(\/|$)/.test(url.pathname)) return "enquiry_form";
  // Exact route only: `/sign-up-something` is not the registration page.
  if (/^\/sign-up\/?$/.test(url.pathname)) return "signup";
  return null;
}

// Staging (`st4g1ng.niftipay.com`) is a Vercel PRODUCTION deployment, so
// `VERCEL_ENV` cannot tell it apart from the live site. The live hosts are
// named instead, which also covers previews and localhost by exclusion.
const LIVE_GA_HOSTS: ReadonlySet<string> = new Set(["www.niftipay.com", "niftipay.com"]);

/** True only on the live marketing hosts; everywhere else the events dry-run. */
export function isLiveGaHost(hostname: string | null | undefined): boolean {
  return typeof hostname === "string" && LIVE_GA_HOSTS.has(hostname.trim().toLowerCase());
}

// A type alias, not an interface: `sendGaEvent` takes a
// `Record<string, string>`, and only an alias gets the implicit index
// signature that makes it assignable.
export type CommercialCtaParams = {
  cta_placement: CommercialPlacement;
  cta_label: string;
  cta_destination_type: CtaDestinationType;
};

/**
 * The whole `commercial_cta_click` decision for one clicked link. `null` means
 * send nothing.
 *
 * `marker` is the `data-commercial-cta` / `data-cta-placement` value of the
 * nearest marked ancestor, or `null` when the link has no marked ancestor at
 * all — which is the difference between an in-content Telegram CTA and site
 * furniture pointing at the same handle.
 */
export function commercialCtaEvent(
  href: string | null | undefined,
  marker: string | null,
  label: string,
): CommercialCtaParams | null {
  const destinationType = ctaDestinationType(href);
  if (destinationType === null) return null;
  // `/sign-up` and the enquiry form are commercial wherever they appear. A
  // `t.me` link is not: the floating support bubble, the footer and the FAQ
  // contact row all point at the same handle and are support, not a CTA. So
  // Telegram counts only where the page content marks it as one.
  if (destinationType === "telegram" && marker === null) return null;
  return {
    cta_placement: commercialPlacement(marker),
    cta_label: label,
    cta_destination_type: destinationType,
  };
}

/** `sessionStorage` key carrying the CTA placement that led to `/sign-up`. */
export const REFERRING_CTA_KEY = "np_referring_cta_placement";

/** `sessionStorage` key marking `signup_intent` as already sent this session. */
export const SIGNUP_INTENT_KEY = "np_signup_intent_sent";

/**
 * `sessionStorage` key set by the sign-up success branch and consumed once on
 * the `/verify-email` mount. The flag, not the page view, is what makes
 * `sign_up` fire — so a reload of `/verify-email`, a direct visit or the link
 * in the verification email produce nothing.
 */
export const SIGNUP_OK_KEY = "np_signup_ok";

/**
 * How long a stored referring placement and a stored sign-up confirmation stay
 * usable. Matches the registered "Referring CTA placement" dimension, which is
 * defined as the last signup CTA clicked within 30 minutes.
 */
export const COMMERCIAL_TTL_MS = 30 * 60 * 1000;

/** `<placement>|<epoch ms>`, so the value carries its own expiry. */
export function formatStamped(value: string, now: number): string {
  return `${value}|${now}`;
}

function readStamped(raw: string | null | undefined, now: number): string | null {
  if (typeof raw !== "string") return null;
  const separator = raw.lastIndexOf("|");
  if (separator < 1) return null;
  const stamp = Number(raw.slice(separator + 1));
  if (!Number.isFinite(stamp)) return null;
  // A clock that moved backwards (a stamp in the future) is treated as stale
  // rather than as valid forever.
  const age = now - stamp;
  if (age < 0 || age > COMMERCIAL_TTL_MS) return null;
  return raw.slice(0, separator);
}

/**
 * The stored referring placement, or `"unknown"` when there is none, it has
 * expired, or it was never a placement this app writes — so a stale value can
 * never be attributed to a later registration.
 */
export function parseReferringCta(raw: string | null | undefined, now: number): CommercialPlacement {
  return commercialPlacement(readStamped(raw, now));
}

/** True only for a sign-up confirmation written less than 30 minutes ago. */
export function isFreshSignupConfirmation(raw: string | null | undefined, now: number): boolean {
  return readStamped(raw, now) === "1";
}
